import { useState } from 'react';
import { Trash2, Ban } from 'lucide-react';
import { formatMinutes, parseDuration } from '../../lib/utils/timeFormat';
import type { TaskTimeEntry } from '../../lib/types';

interface Props {
  entry: TaskTimeEntry;
  canEdit: boolean;
  onUpdate: (id: string, updates: Partial<TaskTimeEntry>) => void;
  onDelete: (id: string) => void;
}

/** Una voce di tempo. Durata e nota si modificano in linea, le azioni compaiono in hover. */
export function TaskTimeEntryRow({ entry, canEdit, onUpdate, onDelete }: Props) {
  const [note, setNote] = useState(entry.description ?? '');
  const [editingNote, setEditingNote] = useState(false);
  const [duration, setDuration] = useState(formatMinutes(entry.duration_minutes));
  const [editingDuration, setEditingDuration] = useState(false);

  const initials = (entry.user?.full_name ?? '?')
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const day = new Date(entry.started_at).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit'
  });

  const commitDuration = () => {
    setEditingDuration(false);
    const minutes = parseDuration(duration);
    if (minutes > 0 && minutes !== entry.duration_minutes) {
      onUpdate(entry.id, { duration_minutes: minutes });
    } else {
      // input incomprensibile o invariato: si torna al valore vero senza toccare il dato
      setDuration(formatMinutes(entry.duration_minutes));
    }
  };

  return (
    <div className="group flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] transition-colors">
      <span className="w-11 flex-shrink-0 text-xs text-[#787774] dark:text-gray-400">{day}</span>

      {editingDuration ? (
        <input
          autoFocus
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
          onBlur={commitDuration}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setDuration(formatMinutes(entry.duration_minutes));
              setEditingDuration(false);
            }
          }}
          className="w-16 flex-shrink-0 text-sm bg-transparent border-none outline-none focus:ring-0 p-0 text-[#37352F] dark:text-gray-200"
        />
      ) : (
        <span
          onClick={() => canEdit && setEditingDuration(true)}
          title={canEdit ? 'Clicca per correggere la durata' : undefined}
          className={`w-16 flex-shrink-0 text-sm ${canEdit ? 'cursor-text' : ''} ${
            entry.counts_toward_budget
              ? 'text-[#37352F] dark:text-gray-200'
              : 'text-[#787774] dark:text-gray-500 line-through'
          }`}
        >
          {formatMinutes(entry.duration_minutes)}
        </span>
      )}

      {editingNote ? (
        <input
          autoFocus
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => {
            setEditingNote(false);
            if (note !== (entry.description ?? '')) onUpdate(entry.id, { description: note });
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              setNote(entry.description ?? '');
              setEditingNote(false);
            }
          }}
          className="flex-1 min-w-0 text-sm bg-transparent border-none outline-none focus:ring-0 p-0 text-[#37352F] dark:text-gray-200"
        />
      ) : (
        <span
          onClick={() => canEdit && setEditingNote(true)}
          className={`flex-1 min-w-0 truncate text-sm text-[#37352F] dark:text-gray-300 rounded px-1 -mx-1 ${
            canEdit ? 'cursor-text hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-700/50' : ''
          }`}
        >
          {entry.description || (
            <span className="text-[#787774] dark:text-gray-500">Senza nota</span>
          )}
        </span>
      )}

      <span
        title={entry.user?.full_name ?? undefined}
        className="w-7 h-5 flex-shrink-0 flex items-center justify-center text-[10px] font-medium rounded bg-[rgba(55,53,47,0.06)] dark:bg-gray-700 text-[#787774] dark:text-gray-300"
      >
        {initials}
      </span>

      {canEdit && (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            title={
              entry.counts_toward_budget
                ? 'Escludi dal monte ore: resta tracciata ma il cliente non la vede'
                : 'Includi nel monte ore'
            }
            onClick={() =>
              onUpdate(entry.id, { counts_toward_budget: !entry.counts_toward_budget })
            }
            className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700"
          >
            <Ban
              className={`w-3.5 h-3.5 ${
                entry.counts_toward_budget ? 'text-[#787774]' : 'text-amber-600'
              }`}
            />
          </button>
          <button
            type="button"
            title="Elimina"
            onClick={() => {
              // Tempo di lavoro già registrato: una volta cancellato non si recupera
              const quando = new Date(entry.started_at).toLocaleDateString('it-IT');
              if (
                window.confirm(
                  `Eliminare ${formatMinutes(entry.duration_minutes)} registrati il ${quando}?`
                )
              ) {
                onDelete(entry.id);
              }
            }}
            className="p-1 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-gray-700"
          >
            <Trash2 className="w-3.5 h-3.5 text-[#787774]" />
          </button>
        </div>
      )}
    </div>
  );
}
